import React, { useMemo } from "react";
import { AHIcon, AHText, Box, useCircleSize, Image } from "arkh";
import { View } from "react-native";
import { graphql, useFragment } from "react-relay";
import { ListItemDescriptionContentFiles_files$key } from "./__generated__/ListItemDescriptionContentFiles_files.graphql";
import { FileIconBox } from "../../../Files/FileListItem/FileIcon";
import { ListItemDescriptionContent_images$key } from "./__generated__/ListItemDescriptionContent_images.graphql";

import { ListItemDescriptionContentProducts_products$key } from "./__generated__/ListItemDescriptionContentProducts_products.graphql";
import { WorkspaceTodoListItemProducts } from "../../../OrgHome/WorkspaceTodos/WorkspaceTodoListItemProducts";

function ListItemDescriptionMediaRowItemContainer(props: {
  children: React.ReactNode;
  isFirst: boolean;
}) {
  const size = useCircleSize("smallMedium");
  const borderWidth = 2;
  return (
    <Box
      color="surface-background"
      cornerRadius="medium"
      borderColor="surface-primary"
      style={{
        width: size + borderWidth * 2,
        height: size + borderWidth * 2,
        borderWidth,
        marginTop: -borderWidth,
        marginLeft: !props.isFirst ? -size / 4 : 0,
        overflow: "hidden",
      }}
    >
      {props.children}
    </Box>
  );
}

function ListItemDescriptionContentTitle(props: {
  title: string;
  orgIcon: boolean;
}) {
  return (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      {props.orgIcon ? (
        <AHIcon spaceRight="xsmall" type="org" size="xsmall" />
      ) : null}
      <AHText
        numberOfLines={1}
        type="subtitle"
        style={{ alignSelf: "stretch" }}
      >
        {props.title}
      </AHText>
    </View>
  );
}

function ListItemDescriptionContainer(props: {
  title: React.ReactElement | null;
  mediaRow: React.ReactElement | null;
}) {
  return (
    <Box>
      {props.title}

      {props.mediaRow ? (
        <Box
          style={{ flexDirection: "row", alignItems: "center" }}
          spaceTop="xsmall"
        >
          {props.mediaRow}
        </Box>
      ) : null}
    </Box>
  );
}

export function ListItemDescriptionContent(props: {
  images: ListItemDescriptionContent_images$key;
  files: ListItemDescriptionContentFiles_files$key;
  products: ListItemDescriptionContentProducts_products$key;
  title: string;
  description?: string | null;
  orgIcon?: boolean;
}) {
  const _images = useFragment(
    graphql`
      fragment ListItemDescriptionContent_images on Image @relay(plural: true) {
        id
        ...Image_image
      }
    `,
    props.images
  );

  const images = useMemo(() => {
    return _images.flatMap((i) => (i ? [i] : []));
  }, [_images]);

  const files = useFragment(
    graphql`
      fragment ListItemDescriptionContentFiles_files on File
      @relay(plural: true) {
        id
        ...FileIconBox_file
      }
    `,
    props.files
  );
  const products = useFragment(
    graphql`
      fragment ListItemDescriptionContentProducts_products on WorkspaceProduct
      @relay(plural: true) {
        id
        ...WorkspaceTodoListItemProducts_WorkspaceProduct
      }
    `,
    props.products
  );

  const hasMedia = files.length > 0 || images.length > 0 || products.length > 0;
  return (
    <ListItemDescriptionContainer
      title={
        <ListItemDescriptionContentTitle
          title={props.title}
          orgIcon={props.orgIcon ?? false}
        />
      }
      mediaRow={
        hasMedia ? (
          <Box style={{ flexDirection: "column", flex: 1 }}>
            <Box
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              {files.map((f, index) => (
                <ListItemDescriptionMediaRowItemContainer
                  key={f.id}
                  isFirst={files.length === 0 && index === 0}
                >
                  <FileIconBox
                    file={f}
                    size="smallMedium"
                    resolveOrgToFileType
                  />
                </ListItemDescriptionMediaRowItemContainer>
              ))}
              {images.map((i, index) => (
                <ListItemDescriptionMediaRowItemContainer
                  key={i.id}
                  isFirst={files.length === 0 && index === 0}
                >
                  <Image
                    type="remote"
                    id={i.id}
                    image={i}
                    style={{ flex: 1 }}
                  />
                </ListItemDescriptionMediaRowItemContainer>
              ))}
            </Box>
            {products.length > 0 ? (
              <Box
                spaceTop={
                  files.length > 0 || images.length > 0 ? "xsmall" : "none"
                }
              >
                <WorkspaceTodoListItemProducts products={products} />
              </Box>
            ) : null}
          </Box>
        ) : null
      }
    />
  );
}
